/**
 * Pure Mathematical Continuous Angle Resampler for Billboard Sprites.
 *
 * Implements continuous angle quantization:
 * f(θ, N) = floor((θ / 2π) * N + 0.5) mod N
 *
 * Guarantees 100% layer reachability across all authentic 1998 frame ring densities
 * (N ∈ {4, 8, 17, 22, 34, 65}) without discrete domain truncation or hardcoded conditionals.
 */

export const TWO_PI = Math.PI * 2

/**
 * Normalizes any continuous radian angle into [0, 2π).
 */
export function normalizeAngle(theta: number): number {
  let angle = theta % TWO_PI
  if (angle < 0) {
    angle += TWO_PI
  }
  return angle
}

/**
 * Computes the continuous discrete angle index from viewing angle θ (in radians)
 * over N available rotational frames.
 *
 * @param theta Viewing angle in radians in [0, 2π) where 0 is Front (facing camera)
 * @param numAngles Total number of discrete angular slices (e.g. 4, 8, 17, 22, 34, 65)
 * @returns Zero-indexed layer/angle offset in [0, numAngles - 1]
 */
export function sampleAngleIndex(theta: number, numAngles: number): number {
  if (numAngles <= 1) {
    return 0
  }
  const norm = normalizeAngle(theta)
  const index = Math.floor((norm / TWO_PI) * numAngles + 0.5) % numAngles
  return index
}

/**
 * Calculates continuous relative viewing yaw θ between an observer (camera) and a target entity.
 *
 * @param observerPos Observer position in world coordinates (X, Z)
 * @param entityPos Target entity position in world coordinates (X, Z)
 * @param entityFacingYaw Target entity's facing yaw in radians (default 0)
 * @returns Relative angle in radians [0, 2π)
 */
export function computeRelativeYaw(
  observerPos: { x: number; z: number },
  entityPos: { x: number; z: number },
  entityFacingYaw: number = 0
): number {
  const dx = observerPos.x - entityPos.x
  const dz = observerPos.z - entityPos.z
  const viewAngle = Math.atan2(dx, dz)
  return normalizeAngle(viewAngle - entityFacingYaw)
}

/**
 * Convenience helper to map a continuous viewing angle into standard 8-way cardinal/ordinal octants.
 * Octants:
 * 0: Front (0°)
 * 1: Front-Right (45°)
 * 2: Right (90°)
 * 3: Back-Right (135°)
 * 4: Back (180°)
 * 5: Back-Left (225°)
 * 6: Left (270°)
 * 7: Front-Left (315°)
 */
export function angleToOctant(theta: number): number {
  return sampleAngleIndex(theta, 8)
}

export const OCTANT_NAMES: readonly string[] = [
  'FRONT (0°)',
  'FRONT-RIGHT (45°)',
  'RIGHT (90°)',
  'BACK-RIGHT (135°)',
  'BACK (180°)',
  'BACK-LEFT (225°)',
  'LEFT (270°)',
  'FRONT-LEFT (315°)',
] as const
